import { useEffect, useState } from 'react';
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { queryAnalyticsCounts } from '../../api';

ChartJS.register(ArcElement, Legend, Tooltip);

const ANALYTICS_STARTED_AT = '2026-09-01T00:00:00.000Z';
const PLAYER_COUNT_COLORS = ['#178a37', '#e89b22', '#2684c7', '#d94e41', '#1d6c6c', '#c76b9b', '#6c8f2d', '#5f6b7a'];

const metricDefinitions = [
  { label: 'Games Hosted', surface: 'home', action: 'host_game', category: 'gameplay' },
  { label: 'Games Finished', surface: 'end_scene', action: 'phase_view', category: 'page_view' },
  { label: 'Games Restarted', surface: 'end_scene', action: 'play_again', category: 'gameplay' },
  {
    label: 'Games Shared',
    sources: [
      { surface: 'lobby', action: 'share_lobby', category: 'ui' },
      { surface: 'end_scene', action: 'share_lobby', category: 'ui' }
    ]
  },
  { label: 'Spectators Per Game', surface: 'lobby', action: 'start_game', category: 'gameplay', aggregate: 'average', metadataKey: 'spectators' },
  { label: 'AI Players Added', surface: 'lobby', action: 'add_ai_smart', category: 'gameplay' },
  { label: 'Lobby Settings Opened', surface: 'lobby', action: 'open_settings', category: 'ui' },
  { label: 'Lobby Rules Edited', surface: 'lobby', action: 'toggle_rule', category: 'ui' },
  { label: 'Rulebook Opened', surface: 'game', action: 'open_rulebook', category: 'ui' },
  {
    label: 'Discord Button Clicked',
    sources: [
      { surface: 'home', action: 'open_discord', category: 'ui' },
      { surface: 'end_scene', action: 'open_discord', category: 'ui' }
    ]
  },
  { label: 'Minigame Mode Played', surface: 'minigame', action: 'minigame_started', category: 'gameplay' }
];

function getTotal(buckets) {
  return buckets.reduce((total, bucket) => total + bucket.count, 0);
}

function getAverage(buckets) {
  const totals = buckets.reduce((total, bucket) => ({
    count: total.count + (bucket.count ?? 0),
    sum: total.sum + (bucket.sum ?? 0)
  }), { count: 0, sum: 0 });
  return totals.count === 0 ? 0 : Math.round((totals.sum / totals.count) * 10) / 10;
}

function getPlayerFrequencies(buckets) {
  const frequencies = {};

  for (const bucket of buckets) {
    for (const [players, count] of Object.entries(bucket.frequencies ?? {})) {
      const playerCount = Number(players);
      if (Number.isInteger(playerCount) && playerCount > 0 && Number.isInteger(count) && count > 0) {
        frequencies[playerCount] = (frequencies[playerCount] ?? 0) + count;
      }
    }
  }

  return frequencies;
}

function PlayersPerGameChart() {
  const [state, setState] = useState({ loading: true, frequencies: null });

  useEffect(() => {
    let cancelled = false;

    queryAnalyticsCounts({
      environment: 'prod',
      surface: 'lobby',
      action: 'start_game',
      category: 'gameplay',
      aggregate: 'frequency',
      metadataKey: 'players',
      from: ANALYTICS_STARTED_AT,
      to: new Date().toISOString()
    })
      .then((response) => {
        if (!cancelled) setState({ loading: false, frequencies: getPlayerFrequencies(response.data.buckets) });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, frequencies: null });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const entries = Object.entries(state.frequencies ?? {})
    .map(([players, games]) => ({ players: Number(players), games }))
    .sort((first, second) => first.players - second.players);

  return (
    <section className="players-per-game-chart">
      <h2 className="metrics-heading">Players Per Game</h2>
      {state.loading && <p className="metrics-status">Loading player counts...</p>}
      {!state.loading && state.frequencies === null && <p className="metrics-status">Player-count data could not be loaded.</p>}
      {!state.loading && state.frequencies !== null && entries.length === 0 && <p className="metrics-status">No player-count data yet.</p>}
      {entries.length > 0 && (
        <div className="metrics-chart metrics-pie-chart">
          <Pie
            data={{
              labels: entries.map(({ players }) => `${players} ${players === 1 ? 'Player' : 'Players'}`),
              datasets: [{
                data: entries.map(({ games }) => games),
                backgroundColor: entries.map((_, index) => PLAYER_COUNT_COLORS[index % PLAYER_COUNT_COLORS.length]),
                borderColor: '#ffffff',
                borderWidth: 2
              }]
            }}
            options={{
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'bottom', labels: { usePointStyle: true } },
                tooltip: {
                  callbacks: {
                    label: (context) => `${context.label}: ${context.parsed} ${context.parsed === 1 ? 'game' : 'games'}`
                  }
                }
              }
            }}
          />
        </div>
      )}
    </section>
  );
}

function AiGamesChart() {
  const [state, setState] = useState({ loading: true, totals: null });

  useEffect(() => {
    let cancelled = false;
    const query = (metadataValue) => queryAnalyticsCounts({
      environment: 'prod',
      surface: 'lobby',
      action: 'start_game',
      category: 'gameplay',
      metadataKey: 'hasAi',
      metadataValue,
      from: ANALYTICS_STARTED_AT,
      to: new Date().toISOString()
    });

    Promise.all([query('true'), query('false')])
      .then(([aiResponse, humanResponse]) => {
        if (!cancelled) {
          setState({
            loading: false,
            totals: [getTotal(aiResponse.data.buckets), getTotal(humanResponse.data.buckets)]
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, totals: null });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const [aiGames, humanGames] = state.totals ?? [];

  return (
    <section className="players-per-game-chart">
      <h2 className="metrics-heading">AI vs Human Games</h2>
      {state.loading && <p className="metrics-status">Loading game types...</p>}
      {!state.loading && state.totals === null && <p className="metrics-status">Game-type data could not be loaded.</p>}
      {!state.loading && state.totals !== null && aiGames + humanGames === 0 && <p className="metrics-status">No game-type data yet.</p>}
      {!state.loading && state.totals !== null && aiGames + humanGames > 0 && (
        <div className="metrics-chart metrics-pie-chart">
          <Pie
            data={{
              labels: ['AI Games', 'Human Games'],
              datasets: [{
                data: [aiGames, humanGames],
                backgroundColor: ['#2684c7', '#178a37'],
                borderColor: '#ffffff',
                borderWidth: 2
              }]
            }}
            options={{
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'bottom', labels: { usePointStyle: true } },
                tooltip: {
                  callbacks: {
                    label: (context) => `${context.label}: ${context.parsed} ${context.parsed === 1 ? 'game' : 'games'}`
                  }
                }
              }
            }}
          />
        </div>
      )}
    </section>
  );
}

export function MetricTotal({ label, surface, action, category }) {
  const [total, setTotal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    queryAnalyticsCounts({
      environment: 'prod',
      surface,
      action,
      category,
      from: ANALYTICS_STARTED_AT,
      to: new Date().toISOString()
    })
      .then((response) => {
        if (!cancelled) setTotal(getTotal(response.data.buckets));
      })
      .catch(() => {
        if (!cancelled) setTotal('Unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [action, category, surface]);

  return (
    <span className="metrics-chart-total">Total: <strong>{total ?? '...'}</strong></span>
  );
}

function MetricCount({ definition }) {
  const [state, setState] = useState({ loading: !definition.unavailable, value: null });
  const { action, aggregate, category, metadataKey, metadataValue, playerType, sources, surface, unavailable } = definition;

  useEffect(() => {
    if (unavailable) return undefined;

    let cancelled = false;
    const queries = sources ?? [{ surface, action, category, playerType, metadataKey, metadataValue }];
    Promise.all(queries.map((query) => queryAnalyticsCounts({
      environment: 'prod',
      aggregate,
      ...query,
      from: ANALYTICS_STARTED_AT,
      to: new Date().toISOString()
    })))
      .then((responses) => {
        const value = aggregate === 'average'
          ? getAverage(responses.flatMap((response) => response.data.buckets))
          : responses.reduce((total, response) => total + getTotal(response.data.buckets), 0);
        if (!cancelled) setState({ loading: false, value });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, value: null });
      });

    return () => {
      cancelled = true;
    };
  }, [action, aggregate, category, metadataKey, metadataValue, playerType, sources, surface, unavailable]);

  const value = unavailable
    ? 'Not recorded'
    : state.loading
      ? '...'
      : state.value ?? 'Unavailable';

  return (
    <div className="metric-count">
      <h3>{definition.label}</h3>
      <p>{value}</p>
    </div>
  );
}

function LanguageCount({ language, label }) {
  return (
    <MetricCount
      definition={{
        label,
        action: 'change_language',
        category: 'ui',
        metadataKey: 'language',
        metadataValue: language
      }}
    />
  );
}

export default function MetricsCounts() {
  return (
    <>
      <div className="game-metrics-charts">
        <PlayersPerGameChart />
        <AiGamesChart />
      </div>
      <div className="metrics-count-grid">
        {metricDefinitions.map((definition) => <MetricCount key={definition.label} definition={definition} />)}
      </div>
      <h2 className="metrics-heading">Language Set</h2>
      <div className="metrics-count-grid metrics-language-grid">
        <LanguageCount language="korean" label="Korean" />
        <LanguageCount language="spanish" label="Spanish" />
        <LanguageCount language="chinese" label="Chinese" />
      </div>
    </>
  );
}
